import { useApp } from '../context.js';
import { GOOGLE_API_KEY } from '../presets.js';
import { ChevronDown, ChevronUp, Edit3, HeartIcon, MapPin, Navigation, ShareIcon, Trash2, Upload } from '../icons.jsx';
import CardChips from './CardChips.jsx';
import VisitHistory from './VisitHistory.jsx';
import NotesField from './NotesField.jsx';
import CheckGame from './CheckGame.jsx';
import { isVisited } from '../lib/visits.js';

const IS_IOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function RestaurantCard({ restaurant, index }) {
    const { currentProfile, deletePhoto, deleteRestaurant, expandedId, handlePhotoUpload, openEditModal, openInMaps, setExpandedId, shareRestaurant, t, toggleFavorite, toggleWantToTry } = useApp();
    const iWant = !!(restaurant.wantToTry && restaurant.wantToTry[currentProfile]);
    return (
        <div
            id={`restaurant-${restaurant.id}`}
            className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden card-bounce-in"
            style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
        >
            <div className={`p-4 ${restaurant.isFavorite ? 'favorite-glow' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-gray-800">
                                {restaurant.name}
                            </h3>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(restaurant.id);
                                }}
                                className={`favorite-heart p-1.5 -m-1 ${restaurant.isFavorite ? 'is-favorite' : 'text-gray-300'}`}
                                aria-label={restaurant.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                aria-pressed={!!restaurant.isFavorite}
                            >
                                <HeartIcon size={18} className={restaurant.isFavorite ? 'fill-current' : ''} />
                            </button>
                            {navigator.share && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        shareRestaurant(restaurant);
                                    }}
                                    className={`text-gray-400 ${t.accentHoverText} transition-colors p-1.5 -m-1`}
                                    title="Share"
                                    aria-label={`Share ${restaurant.name}`}
                                >
                                    <ShareIcon size={16} />
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(restaurant);
                                }}
                                className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 -m-1"
                                title="Edit"
                                aria-label={`Edit ${restaurant.name}`}
                            >
                                <Edit3 size={16} />
                            </button>
                        </div>
                        <CardChips restaurant={restaurant} />
                        {!isVisited(restaurant) && (
                            <button
                                onClick={(e) => { e.stopPropagation(); toggleWantToTry(restaurant.id); }}
                                aria-pressed={iWant}
                                className={`mt-2 text-xs font-semibold px-3 py-1.5 rounded-full apple-button ${iWant ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}
                            >
                                {iWant ? '🙋 You want to try this' : '🙋 I want to try this'}
                            </button>
                        )}
                        {/* Show notes preview if exists */}
                        {restaurant.notes && restaurant.notes.trim() && expandedId !== restaurant.id && (
                            <p className="text-xs text-gray-600 mt-1 italic line-clamp-1">
                                💬 {restaurant.notes}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-1">
                        <button
                            onClick={() => setExpandedId(expandedId === restaurant.id ? null : restaurant.id)}
                            className="text-gray-400 hover:text-gray-600 p-2 apple-button touch-target"
                            aria-label={expandedId === restaurant.id ? 'Collapse' : 'Show details'}
                            aria-expanded={expandedId === restaurant.id}
                        >
                            {expandedId === restaurant.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        <button
                            onClick={() => deleteRestaurant(restaurant.id)}
                            className="text-red-400 hover:text-red-600 p-2 apple-button touch-target"
                            aria-label={`Move ${restaurant.name} to trash`}
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                </div>

                {/* Location Card - Enhanced Apple Style */}
                {restaurant.address && (
                    <div className="address-card-enhanced p-3 mb-3">
                        <div className="flex items-start gap-2 mb-2">
                            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <MapPin size={16} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 leading-snug">{restaurant.address}</p>
                            </div>
                        </div>
                        {restaurant.coordinates && GOOGLE_API_KEY !== 'YOUR_API_KEY' && (
                            <div className="mini-map mb-2" onClick={() => openInMaps(restaurant.address)}>
                                <img
                                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${restaurant.coordinates.lat},${restaurant.coordinates.lng}&zoom=15&size=400x150&scale=2&markers=color:red%7C${restaurant.coordinates.lat},${restaurant.coordinates.lng}&key=${GOOGLE_API_KEY}`}
                                    alt="Map preview"
                                    loading="lazy"
                                />
                            </div>
                        )}
                        <button
                            onClick={() => openInMaps(restaurant.address)}
                            className="w-full bg-blue-500 text-white px-3 py-2.5 rounded-xl font-medium text-sm apple-button flex items-center justify-center gap-2"
                        >
                            <Navigation size={16} />
                            {IS_IOS ? 'Open in Apple Maps' : 'Open in Maps'}
                        </button>
                    </div>
                )}

                {expandedId === restaurant.id && (
                    <div className="animate-slideDown space-y-3 mt-3 pt-3 border-t border-gray-100">
                        {/* Photo Upload/Display */}
                        <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">📸 Photo:</h4>
                            {restaurant.photo ? (
                                <div className="relative">
                                    <img 
                                        src={restaurant.photo} 
                                        alt={restaurant.name}
                                        className="w-full rounded-xl object-cover"
                                        style={{ maxHeight: '300px' }}
                                    />
                                    <button
                                        onClick={() => deletePhoto(restaurant.id)}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full apple-button shadow-lg"
                                        aria-label="Delete photo"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ) : (
                                <label className="w-full bg-gradient-to-r from-purple-400 to-pink-400 text-white px-4 py-3 rounded-xl font-semibold apple-button flex items-center justify-center gap-2 cursor-pointer">
                                    <Upload size={20} />
                                    Add Photo
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handlePhotoUpload(restaurant.id, e)}
                                        className="hidden"
                                    />
                                </label>
                            )}
                        </div>

                        {/* Visit History */}
                        <VisitHistory restaurant={restaurant} />

                        {/* 2. Notes Field */}
                        <NotesField restaurant={restaurant} />

                        {/* Check Game - Multiplayer */}
                        <div className="pt-3 border-t border-gray-100">
                            <CheckGame restaurant={restaurant} />

                            {/* Date Added */}
                            {restaurant.dateAdded && (
                                <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                                    <span className="text-xs text-gray-400">
                                        Added {new Date(restaurant.dateAdded).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
